import { borderRadius, color, Icon, Tooltip, typography } from '@primer-io/goat'
import { ChevronRight } from '@primer-io/goat-icons'
import { Link } from 'gatsby'
import React from 'react'
import styled from 'styled-components'
import useSubMenuItems from '../../hooks/useSubMenuItems'

///////////////////////////////////////////
// Item
///////////////////////////////////////////

const ItemTooltipTrigger = styled.div`
	padding: 8px 16px;
`

const ItemIndicator = styled.div`
	position: absolute;
	top: 10px;
	left: 0px;
	width: 4px;
	bottom: 10px;
	background: ${color('coral-500')};

	transform: translateX(${p => (p.active ? '0px' : '-4px')});
	transition: all 300ms ease;
`

const IconContainer = styled.div`
	position: relative;
	display: grid;
	place-items: center;
	flex: none;
	width: 28px;
	height: 28px;

	background: ${p => (p.active ? color('gray-100') : 'transparent')};
	border-radius: ${borderRadius('small')};
`

const ItemTitle = styled.div`
	${typography('title-medium')}
	white-space: nowrap;
	width: 100%;

	opacity: ${p => (p.isOpen ? 1 : 0)};
	transform: translateX(${p => (p.isOpen ? '0px' : '-5px')});
	color: ${p => (p.active ? 'black' : color('gray-700'))};

	transition: all 300ms ease;
`

const ItemRoot = styled(Link)`
	position: relative;
	display: flex;
	align-items: center;

	&:hover {
		${IconContainer} {
			background: ${color('gray-100')};
		}
	}

	&:focus {
		outline: none;

		${IconContainer} {
			outline: 2px solid #005ecc;
		}
	}

	&:focus:not(:focus-visible) {
		${IconContainer} {
			outline: none;
		}
	}
`

const Item = ({ active, icon, isOpen, onClick, onMouseEnter, showChevronRight, title, url }) => (
	<ItemRoot to={url} onClick={onClick} onMouseEnter={onMouseEnter}>
		<ItemIndicator active={active} />

		<Tooltip disabled={true}>
			<Tooltip.Trigger as={ItemTooltipTrigger}>
				<IconContainer active={active}>
					<Icon as={icon} color={active ? 'black' : 'gray-700'} />
				</IconContainer>
			</Tooltip.Trigger>
			<Tooltip.Content>{title}</Tooltip.Content>
		</Tooltip>

		<ItemTitle active={active} isOpen={isOpen}>
			{title}
		</ItemTitle>

		{showChevronRight && (
			<div style={{ marginLeft: 'auto', marginRight: 32 }}>
				<Icon as={ChevronRight} />
			</div>
		)}
	</ItemRoot>
)

const Separator = styled.div`
	background-color: ${color('gray-200')};
	height: 1px;
	margin: 16px ${p => (p.isOpen ? '16px' : '8px')};
	transition: 0.3s linear;
`

///////////////////////////////////////////
// Root
///////////////////////////////////////////

const Root = styled.div`
	background: white;
	border-right: 1px solid ${color('gray-200')};
	overflow: hidden auto;
`

const PrimaryMenuItem = ({ active, isOpen, menuItem, onClick, onMouseEnter, showChevronRight }) => {
	const { sdkItems, secondaryMenuItems } = useSubMenuItems({
		pathname: menuItem?.url ?? '/',
		hash: '',
	})

	const items = sdkItems ? Object.values(sdkItems).flatMap(p => p.versions[p.latest]) : secondaryMenuItems

	return (
		<Item
			{...menuItem}
			active={active}
			isOpen={isOpen}
			onClick={e => onClick?.(e, menuItem, items)}
			onMouseEnter={onMouseEnter}
			showChevronRight={showChevronRight && !!items?.length}
		/>
	)
}

export const PrimaryMenuItems = ({ currentMenuItemUrl, isOpen, menuItems, onClick, onMouseEnter, showChevronRight }) =>
	menuItems.map((menuItem, idx) => {
		if (menuItem.type === 'ITEM')
			return (
				<PrimaryMenuItem
					key={menuItem.title}
					active={currentMenuItemUrl && currentMenuItemUrl === menuItem.url}
					isOpen={isOpen}
					menuItem={menuItem}
					onClick={onClick}
					onMouseEnter={onMouseEnter}
					showChevronRight={showChevronRight}
				/>
			)

		if (menuItem.type === 'SEPARATOR') return <Separator key={idx} isOpen={isOpen} />

		return null
	})

export default function PrimaryMenu({ currentMenuItemUrl, isOpen, menuItems, onClick, onMouseEnter }) {
	return (
		<Root isOpen={isOpen}>
			<PrimaryMenuItems
				menuItems={menuItems}
				currentMenuItemUrl={currentMenuItemUrl}
				isOpen={isOpen}
				onClick={onClick}
				onMouseEnter={onMouseEnter}
			/>
		</Root>
	)
}
